if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) {
          this.initialized = true;
          this.form = this.querySelector('[data-shipping-form]');
          this.input = this.querySelector('[data-shipping-zip]');
          this.button = this.querySelector('[data-shipping-submit]');
          this.message = this.querySelector('[data-shipping-message]');
          this.rates = this.querySelector('[data-shipping-rates]');
          this.rootUrl = this.dataset.rootUrl || '/';

          const savedZip = this.readSavedZip();

          if (savedZip) {
            this.input.value = this.formatZip(savedZip);
          }

          this.input.addEventListener('input', () => {
            this.cancelCalculation();
            this.input.value = this.formatZip(this.input.value);
            this.rates.hidden = true;
            this.rates.replaceChildren();
            this.showMessage('');
          });

          this.form.addEventListener('submit', (event) => {
            this.calculate(event);
          });

          this.onCartUpdated = () => this.scheduleRefresh();
          this.onDocumentReady = () => this.observeCart();
        }

        document.addEventListener(
          'camisudo:cart-updated',
          this.onCartUpdated,
        );

        document.addEventListener(
          'DOMContentLoaded',
          this.onDocumentReady,
          { once: true },
        );

        this.observeCart();

        if (this.normalizeZip(this.input.value).length === 8) {
          this.scheduleRefresh();
        }
      }

      disconnectedCallback() {
        this.cancelCalculation();
        window.clearTimeout(this.refreshTimer);
        this.cartObserver?.disconnect();

        document.removeEventListener(
          'camisudo:cart-updated',
          this.onCartUpdated,
        );

        document.removeEventListener(
          'DOMContentLoaded',
          this.onDocumentReady,
        );
      }

      observeCart() {
        this.cartObserver?.disconnect();

        const cartItems =
          this.closest('cart-drawer')?.querySelector('cart-drawer-items') ||
          document.querySelector('cart-items');

        if (!cartItems) return;

        this.cartObserver = new MutationObserver(() => {
          this.scheduleRefresh();
        });

        this.cartObserver.observe(cartItems, {
          childList: true,
          subtree: true,
        });
      }

      scheduleRefresh() {
        this.cancelCalculation();
        this.rates.hidden = true;
        this.rates.replaceChildren();
        this.showMessage('');

        window.clearTimeout(this.refreshTimer);

        this.refreshTimer = window.setTimeout(() => {
          if (
            this.isConnected &&
            this.normalizeZip(this.input.value).length === 8
          ) {
            this.calculate();
          }
        }, 200);
      }

      cancelCalculation() {
        if (!this.calculation) return;

        this.calculation.controller.abort();
        window.clearTimeout(this.calculation.timer);
        this.calculation = null;
        this.setLoading(false);
      }

      normalizeZip(value) {
        return String(value || '')
          .replace(/\D/g, '')
          .slice(0, 8);
      }

      formatZip(value) {
        const digits = this.normalizeZip(value);

        return digits.length > 5
          ? `${digits.slice(0, 5)}-${digits.slice(5)}`
          : digits;
      }

      provinceFromZip(zip) {
        if (!/^\d{8}$/.test(zip)) return '';

        const prefix = Number(zip.slice(0, 5));

        const ranges = [
          [1000, 19999, 'SP'],
          [20000, 28999, 'RJ'],
          [29000, 29999, 'ES'],
          [30000, 39999, 'MG'],
          [40000, 48999, 'BA'],
          [49000, 49999, 'SE'],
          [50000, 56999, 'PE'],
          [57000, 57999, 'AL'],
          [58000, 58999, 'PB'],
          [59000, 59999, 'RN'],
          [60000, 63999, 'CE'],
          [64000, 64999, 'PI'],
          [65000, 65999, 'MA'],
          [66000, 68899, 'PA'],
          [68900, 68999, 'AP'],
          [69000, 69299, 'AM'],
          [69300, 69399, 'RR'],
          [69400, 69899, 'AM'],
          [69900, 69999, 'AC'],
          [70000, 72799, 'DF'],
          [72800, 72999, 'GO'],
          [73000, 73699, 'DF'],
          [73700, 76799, 'GO'],
          [76800, 76999, 'RO'],
          [77000, 77999, 'TO'],
          [78000, 78899, 'MT'],
          [79000, 79999, 'MS'],
          [80000, 87999, 'PR'],
          [88000, 89999, 'SC'],
          [90000, 99999, 'RS'],
        ];

        return (
          ranges.find(
            ([start, end]) => prefix >= start && prefix <= end,
          )?.[2] || ''
        );
      }

      async calculate(event) {
        event?.preventDefault();
        this.cancelCalculation();

        const zip = this.normalizeZip(this.input.value);
        const province = this.provinceFromZip(zip);

        this.rates.hidden = true;
        this.rates.replaceChildren();

        if (zip.length !== 8 || !province) {
          this.showError('Digite um CEP válido com 8 números.');
          return;
        }

        const request = {
          controller: new AbortController(),
          timedOut: false,
        };

        this.calculation = request;

        const current = () => {
          return this.calculation === request && this.isConnected;
        };

        const signal = request.controller.signal;

        request.timer = window.setTimeout(() => {
          request.timedOut = true;
          request.controller.abort();
        }, 15000);

        this.setLoading(true);
        this.showMessage('Calculando as melhores opções para seu CEP…');

        try {
          const params = new URLSearchParams({
            'shipping_address[country]': 'Brazil',
            'shipping_address[province]': province,
            'shipping_address[zip]': zip,
          });

          const base = this.rootUrl.endsWith('/')
            ? this.rootUrl
            : `${this.rootUrl}/`;

          const prepareUrl =
            `${base}cart/prepare_shipping_rates.json?${params}`;

          const ratesUrl =
            `${base}cart/async_shipping_rates.json?${params}`;

          const prepareResponse = await fetch(prepareUrl, {
            method: 'POST',
            signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!prepareResponse.ok && prepareResponse.status !== 202) {
            throw new Error(await this.responseError(prepareResponse));
          }

          const shippingRates = await this.pollRates(ratesUrl, signal);

          if (!current()) return;

          this.saveZip(zip, province);
          this.renderRates(shippingRates);
          this.trackShipping(shippingRates.length > 0);
        } catch (error) {
          if (!current() || (signal.aborted && !request.timedOut)) {
            return;
          }

          this.showError(
            request.timedOut
              ? 'O cálculo está demorando mais que o normal. Tente novamente em instantes.'
              : 'Não foi possível calcular o frete agora. Tente novamente.',
          );

          this.trackShipping(false);
        } finally {
          window.clearTimeout(request.timer);

          if (current()) {
            this.setLoading(false);
            this.calculation = null;
          }
        }
      }

      async pollRates(url, signal) {
        for (let attempt = 0; attempt < 10; attempt += 1) {
          if (attempt > 0) {
            await this.waitForRates(signal);
          }

          const response = await fetch(url, {
            signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (response.status === 202) continue;

          if (!response.ok) {
            throw new Error(await this.responseError(response));
          }

          const data = await response.json();

          if (Array.isArray(data?.shipping_rates)) {
            return data.shipping_rates;
          }
        }

        throw new Error(
          'O cálculo está demorando mais que o normal. Tente novamente em instantes.',
        );
      }

      waitForRates(signal) {
        return new Promise((resolve, reject) => {
          if (signal?.aborted) {
            return reject(new DOMException('Aborted', 'AbortError'));
          }

          const onAbort = () => {
            window.clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          };

          const timer = window.setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
          }, 650);

          signal?.addEventListener('abort', onAbort, {
            once: true,
          });
        });
      }

      responseError() {
        return 'Não foi possível calcular o frete para esse CEP.';
      }

      rateAmount(rate) {
        const value = rate.price;

        if (
          !['number', 'string'].includes(typeof value) ||
          String(value).trim() === ''
        ) {
          return null;
        }

        const amount = Number(value);

        return Number.isFinite(amount) && amount >= 0
          ? amount
          : null;
      }

      renderRates(shippingRates) {
        if (!shippingRates.length) {
          this.showMessage(
            'Nenhuma opção de entrega foi encontrada para esse CEP.',
          );
          return;
        }

        const fragment = document.createDocumentFragment();

        shippingRates.forEach((rate) => {
          const price = this.rateAmount(rate);

          const row = document.createElement('div');
          row.className =
            `camisudo-shipping-calculator__rate${price === 0 ? ' camisudo-shipping-calculator__rate--free' : ''}`;

          const text = document.createElement('div');

          const name = document.createElement('span');
          name.className = 'camisudo-shipping-calculator__rate-name';
          name.textContent = rate.name || rate.title || 'Entrega';
          text.appendChild(name);

          const detailText =
            rate.delivery_range || rate.estimated_time_in_transit;

          if (detailText) {
            const detail = document.createElement('span');

            detail.className =
              'camisudo-shipping-calculator__rate-detail';

            detail.textContent = String(detailText);
            text.appendChild(detail);
          }

          const priceElement = document.createElement('span');
          priceElement.className =
            'camisudo-shipping-calculator__rate-price';

          priceElement.textContent =
            price === null
              ? 'A confirmar no checkout'
              : price === 0
                ? 'Grátis'
                : new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: rate.currency || 'BRL',
                  }).format(price);

          row.append(text, priceElement);
          fragment.appendChild(row);
        });

        this.rates.replaceChildren(fragment);
        this.rates.hidden = false;

        this.showMessage('Opções disponíveis para o seu carrinho:');
      }

      setLoading(loading) {
        this.button.disabled = loading;
        this.button.textContent = loading ? 'Calculando…' : 'Calcular';
        this.setAttribute('aria-busy', String(loading));
      }

      showMessage(message) {
        this.message.textContent = message;

        this.message.classList.remove(
          'camisudo-shipping-calculator__message--error',
        );
      }

      showError(message) {
        this.message.textContent = message;

        this.message.classList.add(
          'camisudo-shipping-calculator__message--error',
        );

        this.rates.hidden = true;
      }

      clearError() {
        this.message.classList.remove(
          'camisudo-shipping-calculator__message--error',
        );
      }

      readSavedZip() {
        try {
          return (
            window.localStorage.getItem('camisudo_shipping_zip') || ''
          );
        } catch (error) {
          return '';
        }
      }

      saveZip(zip, province) {
        try {
          window.localStorage.setItem('camisudo_shipping_zip', zip);
          window.localStorage.setItem('camisudo_shipping_uf', province);
        } catch (_) {}

        document.dispatchEvent(
          new CustomEvent('camisudo:province-change', {
            detail: {
              province,
              source: 'cep',
            },
          }),
        );
      }

      trackShipping(success) {
        document.dispatchEvent(
          new CustomEvent('camisudo:interaction', {
            detail: {
              name: success
                ? 'shipping_calculated'
                : 'shipping_error',
              source: 'cart',
              result: success ? 'success' : 'error',
            },
          }),
        );
      }
    },
  );
}